// libs
import * as Sentry from '@sentry/react';
import 'moment/locale/nb';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { authenticate, clearOldNonEsts } from './api/auth';
import { ErrorOverlay } from './components/error-overlay/error-overlay';
import { ReactQueryCustomDevtools } from './components/ReactQueryCustomDevtools';
import RootErrorBoundary from './components/RootErrorBoundary';
import { exposeWindowObjects, googleAnalytics, routes, setWidevineLicenseServerUrl } from './config';
// views
import { ScriptService } from './controllers/ScriptService';
// common css
import './index.scss';
import { AlertType, AuthReducerState, SubnetIds } from './interfaces';
import { Providers } from './providers';
import { getAuthToken } from './queries/ats/auth/api';
import { Routes, RoutesLoader } from './routes/routes';
import { store } from './store';
import { tracker } from './tracker';
import { isGuestId } from './utils/appUtils';
import { createFetchProxy } from './utils/fetch-proxy';
import { initializeGuestTracker, initializeUserTracker } from './utils/initialize-tracker';
import './utils/smartbanner';
// actions
import { addAuthStatus, setProductListAndPvrStatus, showAlert } from './views/app/actions';

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://505fb45b87b15ad4ed3d186d72a55156@o4508165427822592.ingest.de.sentry.io/4508177071341648',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

window.isLoaded = true;

exposeWindowObjects();

window.fetch = createFetchProxy(window.fetch);

const inDevEnvironment =
  window.location.href.indexOf('tvbeta') !== -1 ||
  window.location.href.indexOf('atvweb-st3') !== -1 ||
  window.location.href.indexOf('localhost') !== -1;

function HomeView() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    startup().then(() => {
      setIsLoading(false);
    });
  }, []);

  if (isLoading) {
    return (
      <RootErrorBoundary>
        <Providers>
          <RoutesLoader />
        </Providers>
      </RootErrorBoundary>
    );
  }

  return (
    <RootErrorBoundary>
      <Providers>
        <ErrorOverlay />
        <Routes />
        {inDevEnvironment && <ReactQueryCustomDevtools />}
      </Providers>
    </RootErrorBoundary>
  );
}

function render() {
  ReactDOM.render(<HomeView />, document.getElementById('root'));
  document.getElementById('root')!.className = ScriptService.getBrowserType();
}

window.showMessage = function (title: string, text: string[]) {
  store.dispatch(
    showAlert({
      title: title,
      text: text,
      type: AlertType.INFO,
    }),
  );
};

const callback = window.location.pathname.indexOf(routes.authentication.callback) !== -1;
const fakeCallback = window.location.pathname.indexOf(routes.authentication.fakecallback) !== -1;

async function startup() {
  // handle login redirect
  if (callback || fakeCallback) {
    return;
  } else {
    // First identify user
    const auth = await authenticate();

    if (!isGuestId(auth.userID)) {
      const token = await getAuthToken();
      await initializeUserTracker(auth, token.value);
    } else {
      await initializeGuestTracker(auth);
    }

    store.dispatch(addAuthStatus(auth));

    if (!isGuestId(auth.userID)) {
      let subnetId = auth.subnetId ?? (ScriptService.isDKUser() ? SubnetIds.DK : SubnetIds.NO);
      ScriptService.setStoredGuestLocation(subnetId.toString());
      if (!ScriptService._isSafari() && auth.ca && auth.ca.widevine && auth.ca.widevine.LA_Url) {
        setWidevineLicenseServerUrl(auth.ca.widevine.LA_Url);
      }

      if ((store.getState().authReducer as AuthReducerState).loggedInWithCredentials) {
        // after authenticated by username and password
        clearOldNonEsts();
      }

      setProductListAndPvrStatus()(store.dispatch);
    }

    const statisticConsentGiven = Boolean(window.Cookiebot.consent.statistics);

    if (statisticConsentGiven) {
      tracker.enable();
    } else {
      tracker.disable();
    }

    try {
      if (statisticConsentGiven) {
        googleAnalytics.enable();
      } else {
        googleAnalytics.disable();
      }
    } catch (e) {
      console.error(e);
    }

    if (ScriptService.isDKUser() && window.initNielsen) {
      // Documentation: https://engineeringportal.nielsen.com/docs/DCR_Denmark_Video_Browser_SDK
      window.initNielsen();

      // const nielsen = window.NOLBUNDLE?.nlsQ('P171E7F90-D20C-459D-9B4E-66E3E6CAEBB6', 'nlsnInstance', {
      //   nol_sdkDebug: 'debug',
      // });

      const nielsen = window.NOLBUNDLE?.nlsQ('P171E7F90-D20C-459D-9B4E-66E3E6CAEBB6', 'nlsnInstance', {
        enableFpid: statisticConsentGiven,
      });

      window.nielsen = nielsen;
    } else {
      const nielsenScript = document.querySelector('script[data-playbacktracking="nielsen"]');
      if (nielsenScript !== null && nielsenScript?.parentElement !== null) {
        nielsenScript?.parentElement?.removeChild(nielsenScript);
      }
    }
  }
}

render();
